/**
 * AI Script Generation Service
 * Calls backend AI proxy endpoint which uses Replit AI Integrations
 */

export type TemplateCategory = 'education' | 'business' | 'content' | 'developer' | 'interview';
export type ScriptTone = 'professional' | 'casual' | 'energetic';

// Backend AI proxy server URL - use relative path to work with same-origin or set via env
const getApiBaseUrl = (): string => {
  if (process.env.REACT_APP_AI_API_URL) {
    return process.env.REACT_APP_AI_API_URL;
  }
  // Use window.location.origin to get correct host in both dev and production
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return `${origin}/api/ai`;
};

const AI_API_BASE_URL = getApiBaseUrl();

export interface AIScriptParams {
  templateType: string;
  category: TemplateCategory;
  userTopic: string;
  tone: ScriptTone;
  duration: number; // in seconds
  sceneContext?: string;
  sceneTitle?: string;
  keyPoints?: string;
  callToAction?: string;
  audienceLevel?: 'beginner' | 'intermediate' | 'advanced';
  targetPlatform?: string;
  targetAudience?: string;
  contentGoal?: string;
}

export interface AIScriptResponse {
  script: string;
  wordCount: number;
  estimatedDuration: number;
  metadata: {
    generatedAt: number;
    model: string;
    confidence: number;
  };
}

export interface BatchSceneInput {
  originalTitle: string;
  duration: number;
  templateScript?: string;  // Original template script for AI to adapt
  sceneNotes?: string;      // Scene notes/tips from template
  templateName?: string;    // Name of source template
}

export interface BatchSceneResult {
  title: string;
  script: string;
  wordCount: number;
  estimatedDuration: number;
  sceneIndex: number;
}

export interface AIBatchScriptParams {
  templateType: string;
  category: TemplateCategory;
  userTopic: string;
  tone: ScriptTone;
  scenes: BatchSceneInput[];
  keyPoints?: string;
  callToAction?: string;
  audienceLevel?: 'beginner' | 'intermediate' | 'advanced';
  targetPlatform?: string;
  targetAudience?: string;
  contentGoal?: string;
  customFields?: Record<string, string>;
}

export interface AIBatchScriptResponse {
  scenes: BatchSceneResult[];
  metadata: {
    generatedAt: number;
    model: string;
    totalScenes: number;
    successCount?: number;
    failedCount?: number;
    canRetryFailed?: boolean;
  };
}

export interface UnifiedSceneResult extends BatchSceneResult {
  success: boolean;
}

export interface AIUnifiedScriptResponse {
  scenes: UnifiedSceneResult[];
  metadata: {
    generatedAt: number;
    model: string;
    totalScenes: number;
    successCount: number;
    failedCount: number;
    unified: boolean;
    attempts: number;
  };
}

/**
 * AI Script Generation Service
 * Generates video scripts using GPT-5 via backend AI proxy (Replit AI Integrations)
 */
export class AIScriptService {
  /**
   * Generate a script using AI based on template and user context
   */
  static async generateScript(params: AIScriptParams): Promise<AIScriptResponse> {
    try {
      // Call backend AI proxy endpoint
      const response = await fetch(`${AI_API_BASE_URL}/generate-script`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(params),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data: AIScriptResponse = await response.json();
      console.log(`[AIScriptService] Generated ${data.wordCount} words via backend proxy`);
      return data;
    } catch (error) {
      console.error('[AIScriptService] Error generating script:', error);
      throw new Error(`Failed to generate script: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Generate scripts for multiple scenes in a single API call
   * More efficient than calling generateScript multiple times
   * Also generates AI-optimized titles for each scene
   * @deprecated Use generateUnifiedScript for better narrative coherence
   */
  static async generateScriptsBatch(params: AIBatchScriptParams): Promise<AIBatchScriptResponse> {
    try {
      const response = await fetch(`${AI_API_BASE_URL}/generate-scripts-batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(params),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data: AIBatchScriptResponse = await response.json();
      console.log(`[AIScriptService] Batch generated ${data.scenes.length} scripts via backend proxy`);
      return data;
    } catch (error) {
      console.error('[AIScriptService] Error in batch script generation:', error);
      throw new Error(`Failed to generate scripts: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Generate ALL scene scripts in ONE coherent AI call for narrative harmony
   * Uses JSON schema enforcement to guarantee valid, non-empty responses
   * Includes automatic retry with exponential backoff
   */
  static async generateUnifiedScript(params: AIBatchScriptParams): Promise<AIUnifiedScriptResponse> {
    try {
      console.log(`[AIScriptService] Unified script generation for ${params.scenes.length} scenes`);
      
      const response = await fetch(`${AI_API_BASE_URL}/generate-unified-script`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(params),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data: AIUnifiedScriptResponse = await response.json();
      console.log(`[AIScriptService] Unified generation complete: ${data.metadata.successCount}/${data.metadata.totalScenes} scenes`);
      return data;
    } catch (error) {
      console.error('[AIScriptService] Error in unified script generation:', error);
      throw new Error(`Failed to generate unified script: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Regenerate a script with user feedback
   */
  static async regenerateScript(
    originalParams: AIScriptParams,
    feedback: string
  ): Promise<AIScriptResponse> {
    try {
      // Call backend AI proxy regenerate endpoint
      const response = await fetch(`${AI_API_BASE_URL}/regenerate-script`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ originalParams, feedback }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('[AIScriptService] Error regenerating script:', error);
      throw new Error(`Failed to regenerate script: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Validate script length against target duration
   */
  static validateScriptDuration(script: string, targetDuration: number): {
    isValid: boolean;
    actualDuration: number;
    deviation: number;
  } {
    const wordCount = script.split(/\s+/).filter(word => word.length > 0).length;
    const actualDuration = Math.round((wordCount / 150) * 60); // 150 WPM
    const deviation = Math.abs(actualDuration - targetDuration);
    
    // Consider valid if within 20% of target duration
    const isValid = deviation <= targetDuration * 0.2;

    return {
      isValid,
      actualDuration,
      deviation
    };
  }

  /**
   * Estimate reading time for a script
   */
  static estimateReadingTime(script: string, wpm: number = 150): number {
    const wordCount = script.split(/\s+/).filter(word => word.length > 0).length;
    return Math.round((wordCount / wpm) * 60); // return in seconds
  }
}
